import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Idioma } from "./idioma";
import { alCaerLaSesion, api, borrarToken, ErrorApi, guardarToken, leerToken } from "./api";
import { olvidarPaquete } from "./sinConexion";

export interface Usuario {
  id: string;
  clubId: string;
  nombre: string;
  email: string;
  rol: string;
  permisos: Permiso[];
}

export interface Club {
  id: string;
  nombre: string;
  slug: string;
  logoUrl: string | null;
  limiteDiarioMg?: number;
  limiteMensualMg?: number;
  cuotaAnualCentimos?: number;
}

/**
 * Los permisos los decide la API. Aquí solo se guarda la lista efectiva que
 * manda al entrar, para ocultar lo que esta persona no puede usar; la API
 * vuelve a comprobarlo en cada petición.
 */
export type Permiso =
  | "verSocios"
  | "crearSocio"
  | "editarSocio"
  | "registrarAcceso"
  | "dispensar"
  | "cobrarCuota"
  | "recargarCartera"
  | "verCatalogo"
  | "gestionarInventario"
  | "anularDispensacion"
  | "anularCuota"
  | "suspenderSocio"
  | "gestionarCaja"
  | "gestionarGastos"
  | "descuentoManual"
  | "moverGuardado"
  | "verInformes"
  | "ajustarCartera"
  | "gestionarPublicaciones"
  | "gestionarSolicitudes"
  | "venderBarra"
  | "gestionarBarra"
  | "gestionarCatalogo"
  | "gestionarUsuarios"
  | "ajustesClub"
  | "bajaSocio"
  | "importarSocios"
  | "importarProductos"
  | "verAuditoria"
  | "borrarDatos"
  | "copiaSeguridad";

export interface DatosRegistroClub {
  nombreClub: string;
  ciudad: string | null;
  nombre: string;
  email: string;
  password: string;
  codigo?: string;
  /** Aceptacion del aviso legal y la privacidad. El servidor la exige. */
  aceptaCondiciones: true;
  /** El idioma de la pantalla: con el nace el club, y lo cambia en Ajustes. */
  idioma?: Idioma;
}

interface ValorSesion {
  usuario: Usuario | null;
  club: Club | null;
  cargando: boolean;
  entrar: (email: string, password: string, club?: string) => Promise<void>;
  registrarClub: (datos: DatosRegistroClub) => Promise<void>;
  salir: () => Promise<void>;
  puede: (permiso: Permiso) => boolean;
  refrescar: () => Promise<void>;
}

const ContextoSesion = createContext<ValorSesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const consultas = useQueryClient();
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [cargando, setCargando] = useState(true);

  const refrescar = useCallback(async () => {
    if (!leerToken()) {
      setUsuario(null);
      setClub(null);
      setCargando(false);
      return;
    }
    try {
      const datos = await api<{ usuario: Usuario; club: Club }>("/auth/yo");
      setUsuario(datos.usuario);
      setClub(datos.club);
    } catch (fallo) {
      // Solo un 401 invalida la sesión. Si la API no responde (reinicio, red
      // caida) se conserva el token para que el siguiente intento entre solo.
      if (fallo instanceof ErrorApi && fallo.status === 401) borrarToken();
      setUsuario(null);
      setClub(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void refrescar();
  }, [refrescar]);

  /*
   * Si el token deja de valer -caducado, cuenta desactivada o el propietario ha
   * cerrado las sesiones-, la pantalla vuelve a la entrada en el acto.
   *
   * Hace falta decirlo aqui a mano: `api()` borra el token del navegador, pero
   * React no se entera y seguiria pintando la pantalla con lo ultimo que
   * cargo. En el mostrador eso significa el listado de socios a la vista de
   * quien este delante, que es justo lo que se queria evitar. Se vacia tambien
   * lo que react-query tenga guardado, porque si no, al volver a entrar se ve
   * un parpadeo con los datos del anterior.
   */
  useEffect(
    () =>
      alCaerLaSesion(() => {
        setUsuario(null);
        setClub(null);
        consultas.clear();
        // Y la copia de socios que el mostrador guarda para trabajar sin red:
        // si no, con la WiFi apagada la tablet sigue enseñando el listado
        // entero aunque la sesion este cerrada.
        olvidarPaquete();
      }),
    [consultas],
  );

  const entrar = useCallback(async (email: string, password: string, slugClub?: string) => {
    const datos = await api<{ token: string; usuario: Usuario; club: Club }>("/auth/login", {
      metodo: "POST",
      cuerpo: { email, password, club: slugClub },
    });
    guardarToken(datos.token);
    setUsuario(datos.usuario);
    setClub(datos.club);
  }, []);

  const registrarClub = useCallback(async (datos: DatosRegistroClub) => {
    const respuesta = await api<{ token: string; usuario: Usuario; club: Club }>("/auth/registrar-club", {
      metodo: "POST",
      cuerpo: datos,
    });
    guardarToken(respuesta.token);
    setUsuario(respuesta.usuario);
    setClub(respuesta.club);
  }, []);

  const salir = useCallback(async () => {
    await api("/auth/logout", { metodo: "POST" }).catch(() => undefined);
    borrarToken();
    setUsuario(null);
    setClub(null);
  }, []);

  const puede = useCallback(
    (permiso: Permiso) => usuario?.permisos?.includes(permiso) ?? false,
    [usuario],
  );

  const valor = useMemo(
    () => ({ usuario, club, cargando, entrar, registrarClub, salir, puede, refrescar }),
    [usuario, club, cargando, entrar, registrarClub, salir, puede, refrescar],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}

export function useSesion(): ValorSesion {
  const valor = useContext(ContextoSesion);
  if (!valor) throw new Error("useSesion debe usarse dentro de ProveedorSesion");
  return valor;
}
